import React, { useState } from 'react';
import { updateValuePickerFilters } from '../../../../actions/reduxActions/valuePickerFilters';
import { fetchTypes, setTypes } from '../../../../actions/reduxActions/typeElements';
import { connect } from 'react-redux';
import { contentTypes, ACCESS_LEVELS } from '../../../../constants';
import Loader from '../../../Loader';

/**
 * @param {Array} typeElements A list of values/types to be displayed
 * @param {Boolean} isLoadingTypes Whether to display a loading indicator on type filter
 * @param {Function} updateFilters Update the filters in the store
 * @param {Object} valuePickerFilters Object describing the value filters that have been set
 * @param {Function} fetchTypeList Get the list of Types for the filter
 * @param {Function} setTypeList Set the list of Types for the filter
 * @param {Object} currentUser The current user's information
 * @description A widget for setting filtering options for value references
 */
export const ValuePickerFilter = ({
    typeElements,
    isLoadingTypes,
    updateFilters,
    valuePickerFilters,
    fetchTypeList,
    setTypeList,
    currentUser,
}) => {
    const [panelState, setPanelState] = useState(false);

    const onFilterChange = (event) => {
        const filterType = event.target.dataset.filter;
        const filterValue = filterType === 'isFixed' ? event.target.checked : event.target.value;

        updateFilters(filterType, filterValue);
    };

    const loadTypes = () => {
        if (!panelState) {
            fetchTypeList('type', currentUser.tenantId, currentUser.authenticationToken);
        } else {
            // Closing the filter panel clears out the current Types so we don't have a stale list
            setTypeList([]);
        }
        setPanelState(!panelState);
    };

    return (
        <React.Fragment>
            <button className="value-picker-filter-toggle" onClick={() => loadTypes()}>
                {panelState ? 'Hide Filters' : 'Show Filters'}
            </button>
            <section className={`value-picker-filter-container ${panelState ? 'is-open' : ''}`}>
                <div>
                    <div className="value-picker-filter-wrapper">
                        <div className="form-group value-picker-filter full-width">
                            <label>Type</label>
                            <div className="mw-select">
                                {isLoadingTypes && <Loader message="Loading types..." />}
                                <select
                                    className="value-picker-filter-select type"
                                    placeholder="Select a type"
                                    value={valuePickerFilters.typeElementId || null}
                                    onChange={onFilterChange}
                                    data-filter="typeElementId"
                                    disabled={isLoadingTypes}
                                >
                                    <option className="none-option" key="none" value="">
                                        &empty; none
                                    </option>
                                    {typeElements.map(
                                        (element) =>
                                            element.id && (
                                                <option key={element.id} value={element.id}>
                                                    {element.developerName}
                                                </option>
                                            ),
                                    )}
                                </select>
                            </div>
                        </div>
                        <div className="form-group value-picker-filter">
                            <label>Content Type</label>
                            <div className="mw-select">
                                <div>
                                    <select
                                        className="value-picker-filter-select content-type"
                                        placeholder="Select a content type"
                                        value={valuePickerFilters.contentType || null}
                                        onChange={onFilterChange}
                                        data-filter="contentType"
                                    >
                                        <option className="none-option" key="none" value="">
                                            &empty; none
                                        </option>
                                        {Object.keys(contentTypes).map((key) => (
                                            <option
                                                key={contentTypes[key]}
                                                value={contentTypes[key]}
                                            >
                                                {key}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="form-group value-picker-filter">
                            <label>Access</label>
                            <div className="mw-select">
                                <div>
                                    <select
                                        className="value-picker-filter-select access"
                                        placeholder="Select a content type"
                                        value={valuePickerFilters.access || null}
                                        onChange={onFilterChange}
                                        data-filter="access"
                                    >
                                        <option className="none-option" key="none" value="">
                                            &empty; none
                                        </option>
                                        {Object.values(ACCESS_LEVELS).map((access) => (
                                            <option key={access.value} value={access.value}>
                                                {access.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="form-group value-picker-filter">
                            <label className="checkbox-label">
                                <input
                                    className="value-picker-filter-checkbox is-fixed"
                                    type="checkbox"
                                    onChange={onFilterChange}
                                    data-filter="isFixed"
                                    checked={valuePickerFilters.isFixed}
                                />
                                Is Fixed
                            </label>
                        </div>
                    </div>
                </div>
            </section>
        </React.Fragment>
    );
};

const mapStateToProps = ({ typeElements, valuePickerFilters, currentUser }) => ({
    typeElements: typeElements.types,
    isLoadingTypes: typeElements.isLoading,
    valuePickerFilters,
    currentUser,
});

const mapDispatchToProps = {
    updateFilters: updateValuePickerFilters,
    fetchTypeList: fetchTypes,
    setTypeList: setTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(ValuePickerFilter);
